class ContactFormValidation {
  constructor(form) {
    this.form = form;
    this.formId = form.id;
    this.init();
  }

  init() {
    // Mark form as initialized to prevent duplicate handlers
    if (this.form.dataset.validationInitialized === 'true') {
      return;
    }
    this.form.dataset.validationInitialized = 'true';

    // Prevent default form submission - use capture phase to ensure we catch it first
    const submitHandler = (e) => {
      // Validate form
      const isValid = this.validate();
      
      if (!isValid) {
        // Prevent form submission
        e.preventDefault();
        e.stopImmediatePropagation();
        e.stopPropagation();
        
        // Return false explicitly
        return false;
      }
    };

    // Add handler in capture phase (true) to catch it early, before other handlers
    this.form.addEventListener('submit', submitHandler, { capture: true, passive: false });

    // Clear errors on input
    const inputs = this.form.querySelectorAll('input, textarea');
    inputs.forEach((input) => {
      input.addEventListener('input', () => {
        this.clearFieldError(input);
      });
    });
  }

  validate() {
    let isValid = true;
    this.clearAllErrors();

    // Validate required fields
    const requiredFields = this.form.querySelectorAll('[aria-required="true"], [required]');
    requiredFields.forEach((field) => {
      if (!this.validateRequired(field)) {
        isValid = false;
      }
    });

    // Validate email format
    const emailFields = this.form.querySelectorAll('input[type="email"]');
    emailFields.forEach((field) => {
      if (field.value && !this.validateEmail(field.value)) {
        this.showFieldError(field, 'Please enter a valid email address');
        isValid = false;
      }
    });

    // Validate first name and last name if name section exists (for contact-form-2)
    const nameSection = this.form.querySelector('.contact-form__name-section');
    if (nameSection) {
      const firstName = this.form.querySelector('#ContactForm-first-name');
      const lastName = this.form.querySelector('#ContactForm-last-name');
      
      if (firstName && !firstName.value.trim()) {
        this.showFieldError(firstName, 'First name is required');
        isValid = false;
      }
      
      if (lastName && !lastName.value.trim()) {
        this.showFieldError(lastName, 'Last name is required');
        isValid = false;
      }
    } else {
      // Validate name field for original contact-form (only if name section doesn't exist)
      const nameField = this.form.querySelector('#ContactForm-name');
      if (nameField && !nameField.value.trim()) {
        const label = this.getFieldLabel(nameField);
        const errorMessage = label ? `${label} is required` : 'Name is required';
        this.showFieldError(nameField, errorMessage);
        isValid = false;
      }
    }

    // Focus first invalid field
    if (!isValid) {
      const firstError = this.form.querySelector('.contact-form__field-error');
      if (firstError) {
        const field = this.form.querySelector(`#${firstError.getAttribute('data-field-id')}`);
        if (field) {
          field.focus();
          field.scrollIntoView({ behavior: 'smooth', block: 'center' });
        }
      }
    }

    return isValid;
  }

  validateRequired(field) {
    const value = field.value.trim();
    if (!value) {
      const label = this.getFieldLabel(field);
      const errorMessage = label ? `${label} is required` : 'This field is required';
      this.showFieldError(field, errorMessage);
      return false;
    }
    return true;
  }

  validateEmail(email) {
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    return emailRegex.test(email);
  }

  getFieldLabel(field) {
    const label = this.form.querySelector(`label[for="${field.id}"]`);
    if (label) {
      // Remove "(required)" or "*" from label text
      return label.textContent.replace(/\s*\(required\)\s*/gi, '').replace(/\s*\*\s*/, '').trim();
    }
    return null;
  }

  showFieldError(field, message) {
    // Remove existing error
    this.clearFieldError(field);

    // Add error class to field
    field.classList.add('contact-form__input--error');
    field.setAttribute('aria-invalid', 'true');

    // Create error container
    const fieldWrapper = field.closest('.contact-form__field-wrapper');
    if (!fieldWrapper) return;

    const errorId = `${field.id}-error`;
    field.setAttribute('aria-describedby', errorId);

    const errorContainer = document.createElement('div');
    errorContainer.className = 'contact-form__field-error';
    errorContainer.id = errorId;
    errorContainer.setAttribute('role', 'alert');
    errorContainer.setAttribute('data-field-id', field.id);

    // Create error icon
    const errorIcon = document.createElement('div');
    errorIcon.className = 'contact-form__error-icon';
    errorIcon.innerHTML = `
    <svg width="17" height="17" viewBox="0 0 17 17" fill="none" xmlns="http://www.w3.org/2000/svg">
<path d="M8.25 5.29183V8.62516M8.25 15.75C4.10786 15.75 0.75 12.3921 0.75 8.25C0.75 4.10786 4.10786 0.75 8.25 0.75C12.3921 0.75 15.75 4.10786 15.75 8.25C15.75 12.3921 12.3921 15.75 8.25 15.75ZM8.2915 11.1252V11.2085L8.2085 11.2082V11.1252H8.2915Z" stroke="white" stroke-width="1.5" stroke-linecap="round" stroke-linejoin="round"/>
</svg>

    `;

    // Create error text
    const errorText = document.createElement('span');
    errorText.className = 'contact-form__error-text';
    errorText.textContent = message;

    errorContainer.appendChild(errorIcon);
    errorContainer.appendChild(errorText);

    // Insert error before field
    fieldWrapper.insertBefore(errorContainer, field);
  }

  clearFieldError(field) {
    field.classList.remove('contact-form__input--error');
    field.removeAttribute('aria-invalid');
    field.removeAttribute('aria-describedby');

    const errorContainer = this.form.querySelector(`#${field.id}-error`);
    if (errorContainer) {
      errorContainer.remove();
    }
  }

  clearAllErrors() {
    const errorContainers = this.form.querySelectorAll('.contact-form__field-error');
    errorContainers.forEach((error) => {
      error.remove();
    });

    const errorFields = this.form.querySelectorAll('.contact-form__input--error');
    errorFields.forEach((field) => {
      field.classList.remove('contact-form__input--error');
      field.removeAttribute('aria-invalid');
      field.removeAttribute('aria-describedby');
    });
  }
}

// Initialize validation for all contact forms
function initializeContactFormValidation() {
  const contactForms = document.querySelectorAll('form[id^="ContactForm-"]');
  contactForms.forEach((form) => {
    if (!form.dataset.validationInitialized) {
      try {
        new ContactFormValidation(form);
      } catch (error) {
        console.error('Error initializing contact form validation:', error);
      }
    }
  });
}

// Initialize immediately if DOM is ready, otherwise wait
if (document.readyState === 'loading') {
  document.addEventListener('DOMContentLoaded', initializeContactFormValidation);
} else {
  // DOM already loaded - initialize immediately
  initializeContactFormValidation();
}

// Also initialize after a short delay to catch any late-loading forms
setTimeout(initializeContactFormValidation, 100);

// Also handle dynamically added forms in Shopify theme editor
if (typeof Shopify !== 'undefined' && Shopify.designMode) {
  document.addEventListener('shopify:section:load', () => {
    setTimeout(initializeContactFormValidation, 100);
  });
  
  document.addEventListener('shopify:block:select', () => {
    setTimeout(initializeContactFormValidation, 100);
  });
}
